import { useState, useEffect } from 'react';
import { useParams, useLocation } from 'wouter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PortfolioTab } from '../components/PortfolioTab';
import { SuiteIntakeTab } from '../components/SuiteIntakeTab';
import { FileNotesTab } from '../components/FileNotesTab';
import { ComplianceTab } from '../components/ComplianceTab';
import { AdviceTab } from '../components/AdviceTab';
import { QASkeleton } from '../components/QASkeleton';
import { QAEmpty } from '../components/QAEmpty';
import { CompactStatCard, StatusBadge, DenseAccordion, DataRow } from '../components/CompactUI';
import {
  EditableField,
  fmtCentsAsDollars,
  editCentsAsDollars,
} from '../components/EditableField';
import { EditableRichField } from '../components/EditableRichField';
import { NextTaskDialog } from '../components/NextTaskDialog';
import { formatAUD, formatDateAU, calculateAge, type AgeResult } from '../../../shared/format';
import { calculateTaxAU2024, calculateFinancialSurplus, deriveMemberFinancials } from '../../../shared/calculations';
import { useAuth } from '../context/AuthContext';
import { can } from '../lib/permissions';
import { Phone, Mail, MapPin, MessageSquare, ExternalLink, Calendar, Clock, Archive } from 'lucide-react';
import type { ClientFullResponse, ClientWithCompliance, TransactionRow, PortfolioSnapshot, HouseholdMemberWithDerived, FeeMetrics } from '../../../shared/api';

const RISK_PROFILE_OPTIONS = [
  { value: 'Conservative', label: 'Conservative' },
  { value: 'Moderately Conservative', label: 'Moderately Conservative' },
  { value: 'Balanced', label: 'Balanced' },
  { value: 'Growth', label: 'Growth' },
  { value: 'High Growth', label: 'High Growth' },
];

const SEGMENT_OPTIONS = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
  { value: 'D', label: 'D' },
  { value: 'inactive', label: 'Inactive' },
];

type TabKey = 'dashboard' | 'portfolio' | 'transactions' | 'notes' | 'advice' | 'compliance' | 'intake' | 'documents';

export function ClientDetail() {
  const params = useParams<{ id: string }>();
  const [, setLocation] = useLocation();
  const [tab, setTab] = useState<TabKey>('dashboard');
  const { user } = useAuth();
  const qc = useQueryClient();
  const [quickMsg, setQuickMsg] = useState<string | null>(null);
  const [dialogKind, setDialogKind] = useState<'call' | 'schedule' | null>(null);
  const [localTime, setLocalTime] = useState(new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));

  useEffect(() => {
    const timer = setInterval(() => {
      setLocalTime(new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['client', params.id, 'full'],
    queryFn: () => api<{ data: ClientFullResponse }>(`/api/clients/${params.id}/full`),
  });

  if (isLoading) return <QASkeleton variant="detail" />;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load client</div>;

  const c = data.data.client;
  const portfolio = data.data.portfolio;

  const segmentVariants: Record<string, 'diamond' | 'gold' | 'silver' | 'prospect'> = {
    Diamond: 'diamond',
    Platinum: 'gold',
    Gold: 'gold',
    Silver: 'silver',
    Bronze: 'prospect',
  };

  const age = calculateAge(c.dateOfBirth);
  const tax = calculateTaxAU2024(c.annualIncomeCents ?? 0);
  const surplus = calculateFinancialSurplus({
    annualIncomeCents: c.annualIncomeCents ?? 0,
    lifestyleExpensesCents: c.lifestyleExpensesCents ?? 0,
    taxPayableCents: tax
  });

  const canEditClient = can(user?.role, 'client.edit');

  async function archiveClient() {
    if (!window.confirm(`Archive ${c.firstName} ${c.surname}? They'll drop off the active list but can be restored from the dashboard's Archived view.`)) return;
    try {
      await api(`/api/clients/${c.id}/archive`, { method: 'POST' });
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
      setLocation('/');
    } catch {
      setQuickMsg('Could not archive this client.');
      window.setTimeout(() => setQuickMsg(null), 4000);
    }
  }

  return (
    <div className="flex flex-col h-full -mt-6">
      {/* Sticky Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-hairline py-3 px-6 lg:px-10 -mx-6 lg:-mx-10 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <StatusBadge label={c.segment ?? 'Unsegmented'} variant={segmentVariants[c.segment ?? ''] ?? 'neutral'} />
                <span className="text-xs font-mono text-ink-mute uppercase tracking-widest">{c.id.split('-')[0]}</span>
              </div>
              <h1 className="text-xl font-bold text-ink flex items-center gap-2">
                {c.surname.toUpperCase()}, {c.firstName}
                {c.informalName && <span className="text-ink-mute font-normal text-lg">({c.informalName})</span>}
              </h1>
            </div>
            <div className="h-10 w-px bg-hairline mx-2" />
            <div className="flex flex-col">
              <span className="text-[10px] uppercase text-ink-mute font-bold tracking-tight flex items-center gap-1">
                <Clock size={10} /> {c.timezone || 'Australia/Sydney'}
              </span>
              <span className="text-sm font-mono font-bold text-ink">{localTime}</span>
            </div>
          </div>

          <div className="flex items-center gap-2">
             {/* Alert Badges */}
             <div className="flex gap-1 mr-4">
                {c.soaNeeded && <div className="w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] flex items-center justify-center font-bold animate-pulse" title="SoA Outstanding">!</div>}
                {c.reviewDue && new Date(c.reviewDue) < new Date() && <div className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center font-bold" title="Review Overdue">R</div>}
             </div>

             {/* Quick Actions */}
             <div className="flex gap-1">
                <button
                  type="button"
                  disabled={!canEditClient}
                  onClick={() => setDialogKind('call')}
                  aria-label="Log a call (creates a task in Next-Task)"
                  title="Log a call (creates a task in Next-Task)"
                  className={`p-2 border border-hairline rounded transition-colors ${canEditClient ? 'hover:bg-slate-50 text-ink' : 'text-ink-mute/60 cursor-not-allowed'}`}
                >
                  <Phone size={14} />
                </button>
                <a
                  href={c.email1 ? `mailto:${c.email1}` : undefined}
                  aria-label={c.email1 ? `Email ${c.email1}` : 'No email address on file'}
                  title={c.email1 ? `Email ${c.email1}` : 'No email address on file'}
                  className={`p-2 border border-hairline rounded transition-colors inline-flex items-center justify-center ${c.email1 ? 'hover:bg-slate-50 text-ink' : 'text-ink-mute/60 pointer-events-none'}`}
                >
                  <Mail size={14} />
                </a>
                <button
                  type="button"
                  aria-label="Add file note"
                  title="Add file note"
                  onClick={() => setTab('notes')}
                  className="p-2 border border-hairline rounded hover:bg-slate-50 transition-colors"
                >
                  <MessageSquare size={14} />
                </button>
                <button
                  type="button"
                  disabled={!canEditClient}
                  onClick={() => setDialogKind('schedule')}
                  aria-label="Schedule a follow-up (creates a task in Next-Task)"
                  title="Schedule a follow-up (creates a task in Next-Task)"
                  className={`p-2 border border-hairline rounded transition-colors ${canEditClient ? 'hover:bg-slate-50 text-ink' : 'text-ink-mute/60 cursor-not-allowed'}`}
                >
                  <Calendar size={14} />
                </button>
                <button
                  type="button"
                  aria-label="Open documents"
                  title="Open documents"
                  onClick={() => setTab('documents')}
                  className="p-2 border border-hairline rounded hover:bg-slate-50 transition-colors"
                >
                  <ExternalLink size={14} />
                </button>
                {canEditClient && (
                  <button
                    type="button"
                    onClick={archiveClient}
                    aria-label="Archive this client"
                    title="Archive this client"
                    className="p-2 border border-hairline rounded transition-colors text-ink-mute hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200"
                  >
                    <Archive size={14} />
                  </button>
                )}
             </div>
          </div>
        </div>

        {quickMsg && (
          <div className="mt-1 text-[11px] text-rust font-semibold" role="status" aria-live="polite">{quickMsg}</div>
        )}

        {dialogKind && (
          <NextTaskDialog
            clientId={c.id}
            clientName={[c.firstName, c.surname].filter(Boolean).join(' ').trim() || 'client'}
            kind={dialogKind}
            onClose={() => setDialogKind(null)}
            onCompleted={(msg) => {
              setQuickMsg(msg);
              void qc.invalidateQueries({ queryKey: ['client', c.id, 'full'] });
              window.setTimeout(() => setQuickMsg(null), 4000);
            }}
          />
        )}

        {/* Dense Tabs */}
        <div className="flex gap-4 mt-3 border-t border-hairline pt-2 overflow-x-auto no-scrollbar">
          {([
            ['dashboard', 'Snapshot'],
            ['portfolio', 'Holdings'],
            ['transactions', 'Activity'],
            ['notes', 'File Notes'],
            ['advice', 'Strategy'],
            ['compliance', 'Compliance'],
            ['intake', 'Intake'],
            ['documents', 'Vault'],
          ] as Array<[TabKey, string]>).map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`text-[10px] uppercase font-bold tracking-widest pb-1 border-b-2 transition-colors whitespace-nowrap ${tab === k ? 'border-rust text-ink' : 'border-transparent text-ink-mute hover:text-ink'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="py-6">
        {tab === 'dashboard' && <SnapshotDashboard client={c} portfolio={portfolio} members={data.data.members ?? []} feeMetrics={data.data.feeMetrics} age={age} tax={tax} surplus={surplus} canEdit={can(user?.role, 'client.edit')} />}
        {tab === 'portfolio' && <PortfolioTab portfolio={portfolio} />}
        {tab === 'transactions' && <TransactionsTab clientId={c.id} />}
        {tab === 'notes' && <FileNotesTab clientId={c.id} initial={data.data.recentNotes} />}
        {tab === 'advice' && <AdviceTab clientId={c.id} canCreate={can(user?.role, 'advice.create')} canApprove={can(user?.role, 'advice.approve')} />}
        {tab === 'compliance' && <ComplianceTab client={c} canEdit={can(user?.role, 'client.edit')} />}
        {tab === 'intake' && <SuiteIntakeTab clientId={c.id} />}
        {tab === 'documents' && <DocumentsTab />}
      </div>
    </div>
  );
}

function SnapshotDashboard({
  client: c,
  portfolio,
  members,
  feeMetrics,
  age,
  tax,
  surplus,
  canEdit
}: {
  client: ClientWithCompliance;
  portfolio: PortfolioSnapshot;
  members: HouseholdMemberWithDerived[];
  feeMetrics?: FeeMetrics;
  age: AgeResult | null;
  tax: number;
  surplus: number;
  canEdit: boolean;
}) {
  // Worksheet formatters for the household-level editable fields.
  const pctDisplay = (v: string | number | boolean | null | undefined) =>
    v == null || v === '' ? '' : `${(Number(v) * 100).toFixed(2)}%`;
  const pctEdit = (v: string | number | boolean | null | undefined) =>
    v == null || v === '' ? '' : String(Number(v) * 100);
  const pctSerialise = (raw: string) => (raw.trim() === '' ? null : String(Number(raw) / 100));
  const dateDisplay = (v: string | number | boolean | null | undefined) => (v ? formatDateAU(String(v)) : '');

  const qc = useQueryClient();
  const fullKey = ['client', c.id, 'full'] as const;
  const addMember = () => {
    void api(`/api/clients/${c.id}/members`, {
      method: 'POST',
      body: JSON.stringify({ role: 'dependant', sortOrder: members.length }),
    }).then(() => qc.invalidateQueries({ queryKey: fullKey }));
  };
  const removeMember = (id: string) => {
    if (!window.confirm('Remove this household member?')) return;
    void api(`/api/clients/${c.id}/members/${id}`, { method: 'DELETE' })
      .then(() => qc.invalidateQueries({ queryKey: fullKey }));
  };

  const allocByBucket = new Map<string, number>(
    portfolio.allocation.map((sl) => [sl.bucket, sl.pct] as [string, number]),
  );
  const allocRows: Array<{ key: string; label: string; field: string; target: string | null }> = [
    { key: 'equity_au', label: 'Australian', field: 'targetAllocAu', target: c.targetAllocAu },
    { key: 'equity_intl', label: 'International', field: 'targetAllocIntl', target: c.targetAllocIntl },
    { key: 'property', label: 'Property', field: 'targetAllocProperty', target: c.targetAllocProperty },
    { key: 'alt', label: 'Alternative', field: 'targetAllocAlt', target: c.targetAllocAlt },
    { key: 'fi', label: 'Fixed Interest', field: 'targetAllocFi', target: c.targetAllocFi },
    { key: 'cash', label: 'Cash', field: 'targetAllocCash', target: c.targetAllocCash },
  ];
  // Inline-edit handles its own debounced PATCH + react-query cache patch —
  // see EditableField. Save/Cancel buttons are gone; every value is a cell.

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      {/* Top Cards — Net worth + Surplus are derived (read-only).
          Review due + Risk profile are editable in place. */}
      <CompactStatCard
        label="Total Net Worth"
        value={formatAUD(portfolio?.tnw ?? 0)}
        subValue={`${portfolio?.accounts?.length ?? 0} Accounts`}
      />
      <CompactStatCard
        label="Net Annual Surplus"
        value={formatAUD(surplus / 100)}
        subValue={`Tax Est: ${formatAUD(tax / 100)}`}
        variant={surplus < 0 ? 'danger' : 'success'}
      />
      <div className={`p-2 border rounded shadow-sm ${
        c.reviewDue && new Date(c.reviewDue) < new Date()
          ? 'border-rose-200 bg-rose-50/30'
          : 'border-hairline'
      }`}>
        <p className="text-[10px] uppercase tracking-tight text-ink-mute font-semibold mb-0.5">Review Status</p>
        <div className="text-sm font-bold text-ink leading-tight">
          <EditableField
            clientId={c.id}
            fieldName="reviewDue"
            value={c.reviewDue}
            canEdit={canEdit}
            type="date"
            placeholder="Set review date"
            displayFn={v => v ? formatDateAU(String(v)) : ''}
          />
        </div>
        <p className="text-[10px] text-ink-mute truncate mt-0.5">
          {c.reviewDue ? (new Date(c.reviewDue) < new Date() ? 'OVERDUE' : 'Current') : 'None set'}
        </p>
      </div>
      <div className="p-2 border border-hairline rounded shadow-sm">
        <p className="text-[10px] uppercase tracking-tight text-ink-mute font-semibold mb-0.5">Risk Profile</p>
        <div className="text-sm font-bold text-ink leading-tight">
          <EditableField
            clientId={c.id}
            fieldName="riskProfile"
            value={c.riskProfile}
            canEdit={canEdit}
            type="select"
            options={RISK_PROFILE_OPTIONS}
            placeholder="Set risk profile"
          />
        </div>
        <p className="text-[10px] text-ink-mute truncate mt-0.5">
          {c.lastRiskProfileDate ? `Last: ${formatDateAU(c.lastRiskProfileDate)}` : 'No date'}
        </p>
      </div>

      {/* Personal Detail + Compliance Indicators */}
      <div className="lg:col-span-1 space-y-4">
        <section className="bg-white border border-hairline rounded shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-3 py-1.5 border-b border-hairline flex justify-between items-center">
             <span className="text-[10px] font-bold uppercase tracking-tight text-ink">Personal Detail</span>
          </div>
          <div className="p-3 space-y-0.5">
            <DataRow
              label="DOB"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="dateOfBirth"
                  value={c.dateOfBirth}
                  canEdit={canEdit}
                  type="date"
                  displayFn={v => v ? `${formatDateAU(String(v))}${age ? ` · ${age.display}` : ''}` : ''}
                />
              }
            />
            <DataRow
              label="Mobile"
              value={
                <span className="flex items-center gap-1"><Phone size={10} />
                  <EditableField
                    clientId={c.id}
                    fieldName="phoneMobile"
                    value={c.phoneMobile}
                    canEdit={canEdit}
                    type="tel"
                    placeholder="Mobile…"
                  />
                </span>
              }
            />
            <DataRow
              label="Home phone"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="phoneHome"
                  value={c.phoneHome}
                  canEdit={canEdit}
                  type="tel"
                  placeholder="Home…"
                />
              }
            />
            <DataRow
              label="Work phone"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="phoneWork"
                  value={c.phoneWork}
                  canEdit={canEdit}
                  type="tel"
                  placeholder="Work…"
                />
              }
            />
            <DataRow
              label="Email"
              value={
                <span className="flex items-center gap-1"><Mail size={10} />
                  <EditableField
                    clientId={c.id}
                    fieldName="email1"
                    value={c.email1}
                    canEdit={canEdit}
                    type="email"
                    placeholder="Email…"
                  />
                </span>
              }
            />
            {(c.email2 || canEdit) && (
              <DataRow
                label="Email 2"
                value={
                  <EditableField
                    clientId={c.id}
                    fieldName="email2"
                    value={c.email2}
                    canEdit={canEdit}
                    type="email"
                    placeholder="Email…"
                  />
                }
              />
            )}
            <DataRow
              label="Address line"
              value={
                <span className="flex items-center gap-1"><MapPin size={10} />
                  <EditableField
                    clientId={c.id}
                    fieldName="addressLine1"
                    value={c.addressLine1}
                    canEdit={canEdit}
                    placeholder="Address…"
                  />
                </span>
              }
            />
            <DataRow
              label="Suburb"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="suburb"
                  value={c.suburb}
                  canEdit={canEdit}
                  placeholder="Suburb…"
                />
              }
            />
            <DataRow
              label="State"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="state"
                  value={c.state}
                  canEdit={canEdit}
                  placeholder="QLD…"
                />
              }
            />
            <DataRow
              label="Postcode"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="postcode"
                  value={c.postcode}
                  canEdit={canEdit}
                  placeholder="4000…"
                />
              }
            />
            <DataRow
              label="Occupation"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="occupation"
                  value={c.occupation}
                  canEdit={canEdit}
                  placeholder="Occupation…"
                />
              }
              subLabel={c.employer ?? undefined}
            />
            <DataRow
              label="Employer"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="employer"
                  value={c.employer}
                  canEdit={canEdit}
                  placeholder="Employer…"
                />
              }
            />
            <DataRow
              label="Segment"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="segment"
                  value={c.segment}
                  canEdit={canEdit}
                  type="select"
                  options={SEGMENT_OPTIONS}
                />
              }
            />
            <DataRow
              label="Retirement"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="retirementDate"
                  value={c.retirementDate}
                  canEdit={canEdit}
                  type="date"
                  displayFn={v => v ? formatDateAU(String(v)) : ''}
                  placeholder="Set date"
                />
              }
            />
            <DataRow
              label="Annual income"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="annualIncomeCents"
                  value={c.annualIncomeCents}
                  canEdit={canEdit}
                  type="currency"
                  displayFn={fmtCentsAsDollars}
                  editStringFn={editCentsAsDollars}
                  placeholder="$0"
                />
              }
            />
            <DataRow
              label="Lifestyle expenses"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="lifestyleExpensesCents"
                  value={c.lifestyleExpensesCents}
                  canEdit={canEdit}
                  type="currency"
                  displayFn={fmtCentsAsDollars}
                  editStringFn={editCentsAsDollars}
                  placeholder="$0"
                />
              }
            />
          </div>
        </section>

        <section className="bg-white border border-hairline rounded shadow-sm overflow-hidden">
          <div className="bg-slate-50 px-3 py-1.5 border-b border-hairline">
             <span className="text-[10px] font-bold uppercase tracking-tight text-ink">Compliance Indicators</span>
          </div>
          <div className="p-3 space-y-0.5">
            <DataRow label="Risk Profile" value={<ComplianceStatus state={c.compliance.riskProfile} />} />
            <DataRow label="Fact Find" value={<ComplianceStatus state={c.compliance.factFind} />} />
            <DataRow label="SoA Signed" value={<ComplianceStatus state={c.compliance.soaSigned} />} />
            <DataRow label="Fee Consent" value={<ComplianceStatus state={c.compliance.feeConsent} />} />
            <DataRow
              label="SoA signed date"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="soaSignedDate"
                  value={c.soaSignedDate}
                  canEdit={canEdit}
                  type="date"
                  displayFn={v => v ? formatDateAU(String(v)) : ''}
                  placeholder="Set date"
                />
              }
            />
            <DataRow
              label="Last fact find"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="lastFactFindDate"
                  value={c.lastFactFindDate}
                  canEdit={canEdit}
                  type="date"
                  displayFn={v => v ? formatDateAU(String(v)) : ''}
                  placeholder="Set date"
                />
              }
            />
            <DataRow
              label="Last fee consent"
              value={
                <EditableField
                  clientId={c.id}
                  fieldName="lastFeeConsentDate"
                  value={c.lastFeeConsentDate}
                  canEdit={canEdit}
                  type="date"
                  displayFn={v => v ? formatDateAU(String(v)) : ''}
                  placeholder="Set date"
                />
              }
            />
          </div>
        </section>
      </div>

      <div className="lg:col-span-3 space-y-4">
        <div className="bg-white border border-hairline rounded shadow-sm">
           <DenseAccordion title="Strategy & Narrative History" defaultOpen={true}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                 <div>
                    <h4 className="text-[10px] font-bold uppercase text-ink-mute mb-2 flex items-center gap-2">
                       <MessageSquare size={12} /> Goals &amp; Objectives
                    </h4>
                    <EditableRichField
                      clientId={c.id}
                      fieldName="goalsObjectives"
                      value={c.goalsObjectives}
                      canEdit={canEdit}
                      placeholder="Goals…"
                    />
                 </div>
                 <div>
                    <h4 className="text-[10px] font-bold uppercase text-ink-mute mb-2 flex items-center gap-2">
                       <Clock size={12} /> Strategy Notes
                    </h4>
                    <EditableRichField
                      clientId={c.id}
                      fieldName="strategyNotes"
                      value={c.strategyNotes}
                      canEdit={canEdit}
                      placeholder="Strategy…"
                    />
                 </div>
              </div>
              <div className="mt-4">
                 <h4 className="text-[10px] font-bold uppercase text-ink-mute mb-2">Personal History</h4>
                 <EditableRichField
                   clientId={c.id}
                   fieldName="personalHistory"
                   value={c.personalHistory}
                   canEdit={canEdit}
                   placeholder="History…"
                 />
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-[10px] font-bold uppercase text-ink-mute mb-2">Financial History</h4>
                  <EditableRichField
                    clientId={c.id}
                    fieldName="financialHistory"
                    value={c.financialHistory}
                    canEdit={canEdit}
                    placeholder="Financial history…"
                  />
                </div>
                <div>
                  <h4 className="text-[10px] font-bold uppercase text-ink-mute mb-2">Insurance Summary</h4>
                  <EditableRichField
                    clientId={c.id}
                    fieldName="insuranceSummary"
                    value={c.insuranceSummary}
                    canEdit={canEdit}
                    placeholder="Insurance summary…"
                  />
                </div>
              </div>
           </DenseAccordion>


           <DenseAccordion title="Advice Scope">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 mt-2">
                 {[
                   'Superannuation Accumulation', 'Retirement Planning', 'Investment Advice (Super)',
                   'Investment Advice (Non-Super)', 'Cashflow and Budgeting', 'Asset Structuring',
                   'Debt Management', 'Life Insurance', 'Centrelink Entitlements', 'Estate Planning'
                 ].map(area => {
                   const isExcluded = c.exclusions?.includes(area);
                   return (
                     <div key={area} className={`flex items-center gap-2 p-1.5 border border-hairline rounded text-[10px] ${isExcluded ? 'bg-slate-50 opacity-60' : 'bg-white'}`}>
                        <div className={`w-2 h-2 rounded-full ${isExcluded ? 'bg-slate-300' : 'bg-emerald-400'}`} />
                        <span className="font-medium truncate" title={area}>{area}</span>
                     </div>
                   );
                 })}
              </div>
           </DenseAccordion>

           <DenseAccordion title="Household Members" badge={<span className="bg-slate-100 px-1 rounded text-[9px]">{members.length}</span>} defaultOpen={true}>
              {members.length === 0 ? (
                <p className="text-xs text-ink-mute p-2">No household members yet.</p>
              ) : (
                <div className="overflow-x-auto" data-testid="household-members">
                  <table className="w-full text-xs mt-2 whitespace-nowrap">
                    <thead>
                      <tr className="text-left border-b border-hairline text-ink-mute">
                        <th className="font-semibold py-1">Name</th>
                        <th className="font-semibold py-1">Role</th>
                        <th className="font-semibold py-1">Age</th>
                        <th className="font-semibold py-1">Occupation / notes</th>
                        <th className="font-semibold py-1 text-right">Income (inc super)</th>
                        <th className="font-semibold py-1 text-right">Conc. super</th>
                        <th className="font-semibold py-1 text-right">Tax</th>
                        <th className="font-semibold py-1 text-right">Net income</th>
                        <th className="font-semibold py-1 text-right">Lifestyle</th>
                        <th className="font-semibold py-1 text-right">Surplus</th>
                        <th className="font-semibold py-1">Retire</th>
                        <th className="py-1" aria-label="Actions"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {members.map(m => {
                        const isDep = m.role === 'dependant';
                        const a = calculateAge(m.dateOfBirth);
                        const name = [m.title, m.firstName, m.surname].filter(Boolean).join(' ') || '—';
                        const ep = `/api/clients/${c.id}/members/${m.id}`;
                        const patch = (cur: unknown, fn: string, v: unknown) => patchCachedMember(cur, m.id, fn, v);
                        return (
                          <tr key={m.id} className="border-b border-hairline border-dotted last:border-0 hover:bg-slate-50 align-top">
                            <td className="py-1.5 font-medium">{name}</td>
                            <td className="py-1.5 capitalize text-ink-mute">{m.role}{isDep && m.relationship ? ` · ${m.relationship}` : ''}</td>
                            <td className="py-1.5">{a?.display ?? '—'}</td>
                            <td className="py-1.5 max-w-xs whitespace-normal">
                              {isDep
                                ? <EditableField clientId={c.id} fieldName="notes" value={m.notes} canEdit={canEdit} endpoint={ep} cachePatch={patch} placeholder="Notes…" className="italic text-ink-mute" />
                                : <EditableField clientId={c.id} fieldName="occupation" value={m.occupation} canEdit={canEdit} endpoint={ep} cachePatch={patch} placeholder="Occupation" />}
                            </td>
                            <td className="py-1.5 text-right font-mono">{isDep ? '' : <EditableField clientId={c.id} fieldName="incomeIncSuperCents" value={m.incomeIncSuperCents} canEdit={canEdit} type="currency" endpoint={ep} cachePatch={patch} displayFn={fmtCentsAsDollars} editStringFn={editCentsAsDollars} placeholder="$0" />}</td>
                            <td className="py-1.5 text-right font-mono">{isDep ? '' : <EditableField clientId={c.id} fieldName="concessionalSuperCents" value={m.concessionalSuperCents} canEdit={canEdit} type="currency" endpoint={ep} cachePatch={patch} displayFn={fmtCentsAsDollars} editStringFn={editCentsAsDollars} placeholder="$0" />}</td>
                            <td className="py-1.5 text-right font-mono">{isDep ? '' : <EditableField clientId={c.id} fieldName="taxPayableCents" value={m.taxPayableCents} canEdit={canEdit} type="currency" endpoint={ep} cachePatch={patch} displayFn={fmtCentsAsDollars} editStringFn={editCentsAsDollars} placeholder={fmtCentsAsDollars(m.derived.taxPayableCents)} />}</td>
                            <td className="py-1.5 text-right font-mono">{isDep ? '' : formatAUD(m.derived.netIncomeCents / 100)}</td>
                            <td className="py-1.5 text-right font-mono">{isDep ? '' : <EditableField clientId={c.id} fieldName="lifestyleExpensesCents" value={m.lifestyleExpensesCents} canEdit={canEdit} type="currency" endpoint={ep} cachePatch={patch} displayFn={fmtCentsAsDollars} editStringFn={editCentsAsDollars} placeholder="$0" />}</td>
                            <td className={`py-1.5 text-right font-mono ${m.derived.surplusCents < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{isDep ? '' : formatAUD(m.derived.surplusCents / 100)}</td>
                            <td className="py-1.5 font-mono"><EditableField clientId={c.id} fieldName="retirementDate" value={m.retirementDate} canEdit={canEdit} type="date" endpoint={ep} cachePatch={patch} displayFn={dateDisplay} placeholder="—" /></td>
                            <td className="py-1.5 text-right">{canEdit && <button type="button" onClick={() => removeMember(m.id)} className="text-ink-mute hover:text-rose-600 px-1 font-bold" title="Remove member" aria-label={`Remove ${name}`}>×</button>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {canEdit && (
                <button type="button" onClick={addMember} className="mt-2 text-[10px] uppercase tracking-tight font-bold text-rust hover:underline">+ Add member</button>
              )}
           </DenseAccordion>

           {feeMetrics && (
             <DenseAccordion title="Fee & Portfolio Metrics" defaultOpen={true}>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-2">
                   <Metric label="Total FUM" value={formatAUD(feeMetrics.totalFumCents / 100)} />
                   <Metric label="Cash to invest" value={formatAUD(feeMetrics.cashToInvestCents / 100)} />
                   <Metric label="Est. yearly fee" value={formatAUD(feeMetrics.estimatedYearlyFeeCents / 100)} />
                   <Metric label="Target yearly fee" value={formatAUD(feeMetrics.targetYearlyFeeCents / 100)} />
                   <Metric label="Fee variance" value={formatAUD(feeMetrics.feeVarianceCents / 100)} tone={feeMetrics.feeVarianceCents < 0 ? 'neg' : feeMetrics.feeVarianceCents > 0 ? 'pos' : undefined} />
                   <Metric label="Fee variance %" value={`${(feeMetrics.feeVariancePct * 100).toFixed(2)}%`} tone={feeMetrics.feeVarianceCents < 0 ? 'neg' : feeMetrics.feeVarianceCents > 0 ? 'pos' : undefined} />
                   <Metric label="Brokerage (12mo)" value={formatAUD(feeMetrics.trailing12moBrokerageCents / 100)} />
                </div>
                <p className="text-[10px] text-ink-mute mt-2">Estimated fee uses each account&apos;s own rate; target uses the household adviser fee. Computed from current holdings + trailing-12-month transactions.</p>
             </DenseAccordion>
           )}

           <DenseAccordion title="Asset Allocation — actual vs target">
              <div className="overflow-x-auto">
                <table className="w-full text-xs mt-2">
                  <thead>
                    <tr className="text-left border-b border-hairline text-ink-mute">
                      <th className="font-semibold py-1">Asset class</th>
                      <th className="font-semibold py-1 text-right">Actual</th>
                      <th className="font-semibold py-1 text-right">Target</th>
                      <th className="font-semibold py-1 text-right">Drift</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allocRows.map((r) => {
                      const actualPct = (allocByBucket.get(r.key) ?? 0) * 100;
                      const targetPct = r.target != null && r.target !== '' ? Number(r.target) * 100 : null;
                      const drift = actualPct - (targetPct ?? 0);
                      return (
                        <tr key={r.key} className="border-b border-hairline border-dotted last:border-0 hover:bg-slate-50">
                          <td className="py-1.5">{r.label}</td>
                          <td className="py-1.5 text-right font-mono">{actualPct.toFixed(2)}%</td>
                          <td className="py-1.5 text-right font-mono">
                            <EditableField clientId={c.id} fieldName={r.field} value={r.target} canEdit={canEdit} type="number" displayFn={pctDisplay} editStringFn={pctEdit} serialiseFn={pctSerialise} placeholder="—" />
                          </td>
                          <td className={`py-1.5 text-right font-mono ${Math.abs(drift) >= 5 ? 'text-rose-700' : 'text-ink-mute'}`}>{drift >= 0 ? '+' : ''}{drift.toFixed(2)}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p className="text-[10px] text-ink-mute mt-2">Actual % is of holdings value (excludes account cash). Drift of 5%+ is highlighted.</p>
              </div>
           </DenseAccordion>

           <DenseAccordion title="Accountant, Fees & Service">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mt-2">
                 <div className="space-y-0.5">
                    <DataRow label="Accountant" value={<EditableField clientId={c.id} fieldName="accountantName" value={c.accountantName} canEdit={canEdit} placeholder="Name" />} />
                    <DataRow label="Firm" value={<EditableField clientId={c.id} fieldName="accountantFirm" value={c.accountantFirm} canEdit={canEdit} placeholder="Firm" />} />
                    <DataRow label="Acct. email" value={<EditableField clientId={c.id} fieldName="accountantEmail" value={c.accountantEmail} canEdit={canEdit} type="email" placeholder="Email" />} />
                    <DataRow label="Acct. phone" value={<EditableField clientId={c.id} fieldName="accountantPhone" value={c.accountantPhone} canEdit={canEdit} type="tel" placeholder="Phone" />} />
                    <DataRow label="Referred by" value={<EditableField clientId={c.id} fieldName="referredBy" value={c.referredBy} canEdit={canEdit} placeholder="Referrer" />} />
                 </div>
                 <div className="space-y-0.5">
                    <DataRow label="Adviser fee" value={<EditableField clientId={c.id} fieldName="adviserFeeRate" value={c.adviserFeeRate} canEdit={canEdit} type="number" displayFn={pctDisplay} editStringFn={pctEdit} serialiseFn={pctSerialise} placeholder="0.00%" />} />
                    <DataRow label="Brokerage" value={<EditableField clientId={c.id} fieldName="defaultBrokerageRate" value={c.defaultBrokerageRate} canEdit={canEdit} type="number" displayFn={pctDisplay} editStringFn={pctEdit} serialiseFn={pctSerialise} placeholder="0.00%" />} />
                    <DataRow label="Min brokerage" value={<EditableField clientId={c.id} fieldName="minBrokerageCents" value={c.minBrokerageCents} canEdit={canEdit} type="currency" displayFn={fmtCentsAsDollars} editStringFn={editCentsAsDollars} placeholder="$0" />} />
                    <DataRow label="Max brokerage" value={<EditableField clientId={c.id} fieldName="maxBrokerageCents" value={c.maxBrokerageCents} canEdit={canEdit} type="currency" displayFn={fmtCentsAsDollars} editStringFn={editCentsAsDollars} placeholder="$0" />} />
                    <DataRow label="Mac OFA due" value={<EditableField clientId={c.id} fieldName="macOfaDate" value={c.macOfaDate} canEdit={canEdit} type="date" displayFn={dateDisplay} placeholder="Set date" />} />
                    <DataRow label="Shaws OFA due" value={<EditableField clientId={c.id} fieldName="shawsOfaDate" value={c.shawsOfaDate} canEdit={canEdit} type="date" displayFn={dateDisplay} placeholder="Set date" />} />
                    <DataRow label="Scoping" value={<EditableField clientId={c.id} fieldName="scopingDate" value={c.scopingDate} canEdit={canEdit} type="date" displayFn={dateDisplay} placeholder="Set date" />} />
                    <DataRow label="Inv. strategy" value={<EditableField clientId={c.id} fieldName="invStratStatus" value={c.invStratStatus} canEdit={canEdit} placeholder="Status" />} />
                 </div>
              </div>
              <div className="mt-3">
                 <h4 className="text-[10px] font-bold uppercase text-ink-mute mb-1">Postal Address</h4>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
                    <div className="space-y-0.5">
                       <DataRow label="Address 1" value={<EditableField clientId={c.id} fieldName="postalAddressLine1" value={c.postalAddressLine1} canEdit={canEdit} placeholder="Line 1" />} />
                       <DataRow label="Address 2" value={<EditableField clientId={c.id} fieldName="postalAddressLine2" value={c.postalAddressLine2} canEdit={canEdit} placeholder="Line 2" />} />
                       <DataRow label="Suburb" value={<EditableField clientId={c.id} fieldName="postalSuburb" value={c.postalSuburb} canEdit={canEdit} placeholder="Suburb" />} />
                    </div>
                    <div className="space-y-0.5">
                       <DataRow label="State" value={<EditableField clientId={c.id} fieldName="postalState" value={c.postalState} canEdit={canEdit} placeholder="State" />} />
                       <DataRow label="Postcode" value={<EditableField clientId={c.id} fieldName="postalPostcode" value={c.postalPostcode} canEdit={canEdit} placeholder="Postcode" />} />
                    </div>
                 </div>
              </div>
           </DenseAccordion>

           <DenseAccordion title="Flags & Indicators">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 mt-2">
                 {([
                   ['Pension', 'pensionFlag', c.pensionFlag],
                   ['SMSF', 'smsfFlag', c.smsfFlag],
                   ['Property', 'propertyFlag', c.propertyFlag],
                   ['Birthday', 'birthdayFlag', c.birthdayFlag],
                   ['Graphs', 'graphsFlag', c.graphsFlag],
                   ['Reviewed', 'reviewedFlag', c.reviewedFlag],
                   ['Pending advice', 'pendingAdvice', c.pendingAdvice],
                 ] as ReadonlyArray<readonly [string, string, boolean | null]>).map(([label, field, on]) => (
                   <DataRow key={field} label={label} value={<EditableField clientId={c.id} fieldName={field} value={on} canEdit={canEdit} type="boolean" />} />
                 ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 mt-3">
                 <DataRow label="Sells" value={<EditableField clientId={c.id} fieldName="sellsCount" value={c.sellsCount} canEdit={canEdit} type="number" placeholder="0" />} />
                 <DataRow label="Non-APL" value={<EditableField clientId={c.id} fieldName="nonAplCount" value={c.nonAplCount} canEdit={canEdit} type="number" placeholder="0" />} />
              </div>
           </DenseAccordion>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, tone }: { label: string; value: string; tone?: 'pos' | 'neg' }) {
  const colour = tone === 'neg' ? 'text-rose-700' : tone === 'pos' ? 'text-emerald-700' : 'text-ink';
  return (
    <div className="p-2 border border-hairline rounded">
      <p className="text-[10px] uppercase tracking-tight text-ink-mute font-semibold mb-0.5">{label}</p>
      <p className={`text-sm font-bold font-mono ${colour}`}>{value}</p>
    </div>
  );
}

function patchCachedMember(cur: unknown, memberId: string, fieldName: string, value: unknown): unknown {
  if (!cur || typeof cur !== 'object') return cur;
  const obj = cur as { data?: { members?: Array<Record<string, unknown>> } };
  const members = obj.data?.members;
  if (!members) return cur;
  return {
    ...obj,
    data: {
      ...obj.data,
      members: members.map((m) => {
        if (m.id !== memberId) return m;
        const updated: Record<string, unknown> = { ...m, [fieldName]: value };
        const num = (k: string) => (typeof updated[k] === 'number' ? (updated[k] as number) : null);
        const derived = deriveMemberFinancials({
          incomeIncSuperCents: num('incomeIncSuperCents'),
          concessionalSuperCents: num('concessionalSuperCents'),
          investmentIncomeCents: num('investmentIncomeCents'),
          investmentExpensesCents: num('investmentExpensesCents'),
          taxPayableCents: num('taxPayableCents'),
          lifestyleExpensesCents: num('lifestyleExpensesCents'),
        });
        return { ...updated, derived };
      }),
    },
  };
}

function ComplianceStatus({ state }: { state: string }) {
  if (state === 'pass') return <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 shadow-sm" title="Pass" />;
  if (state === 'outstanding') return <div className="w-2.5 h-2.5 rounded-full bg-rose-500 shadow-sm" title="Outstanding" />;
  return <div className="w-2.5 h-2.5 rounded-full bg-slate-300 shadow-sm" title="Unknown" />;
}

function TransactionsTab({ clientId }: { clientId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['client', clientId, 'transactions'],
    queryFn: () => api<{ data: TransactionRow[] }>(`/api/clients/${clientId}/transactions`),
  });
  if (isLoading) return <div className="mt-6"><QASkeleton variant="table" rows={12} /></div>;
  const rows = data?.data ?? [];
  if (rows.length === 0) return <QAEmpty inline title="No transactions yet." />;
  return (
    <div className="overflow-x-auto border border-hairline rounded shadow-sm">
      <table className="qa-table text-xs">
        <thead className="bg-slate-50">
          <tr>
            <th className="py-2">Trade date</th><th>Type</th><th>Security</th>
            <th className="text-right">Units</th><th className="text-right">Price</th>
            <th className="text-right">Total</th><th className="text-right">Fee</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} className="hover:bg-slate-50 transition-colors">
              <td className="py-1.5 font-mono">{formatDateAU(t.trade_date)}</td>
              <td><span className={`px-1 rounded text-[9px] font-bold uppercase ${t.contract_type === 'buy' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>{t.contract_type}</span></td>
              <td><div className="flex flex-col"><span className="font-bold">{t.security_code}</span><span className="text-[10px] text-ink-mute truncate max-w-[150px]">{t.security_name}</span></div></td>
              <td className="text-right font-mono">{Number(t.units).toLocaleString('en-AU')}</td>
              <td className="text-right font-mono">{formatAUD(Number(t.price_cents) / 100)}</td>
              <td className="text-right font-mono font-bold">{formatAUD(Number(t.consideration_cents) / 100)}</td>
              <td className="text-right font-mono text-ink-mute">{formatAUD(Number(t.brokerage_cents) / 100)}</td>
              <td><span className="text-[10px] uppercase text-ink-mute">{t.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DocumentsTab() {
  return (
    <div className="bg-white border border-hairline rounded p-12 text-center text-ink-mute shadow-sm">
      <div className="mx-auto w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-4">
        <ExternalLink size={24} className="text-slate-300" />
      </div>
      <p className="text-sm font-bold text-ink mb-1">Secure Vault</p>
      <p className="text-xs">SharePoint / OneDrive integration pending.</p>
    </div>
  );
}
